import { InternalOrExternalLink } from '@shared/Frontend/components/link'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { slugify } from '@shared/Frontend/utils/slugify'
import { translate } from '@shared/Frontend/utils/translate'
import * as React from 'react'
import { InnerHtmlWrapper } from '../../../../../../../EntryPoint/Frontend/legacy/public_site/components/utils/InnerHtmlWrapper'
import { OfficeBlock } from '../../blocks.state'

// TODO: when office block gets redesigned, created separate Nested layout (for layout split)
export const OfficeBlockLayout = (props: OfficeBlock & { id: string; nested: boolean }) => {
  const { id, label, title, intro, address, contact, body, nested } = props
  const { visiting, postal } = address

  const { inView, ref } = useInView({ threshold: 0.1 })

  return nested ? (
    <section id={id} ref={ref} className={cn('block block--office', inView && 'inview')}>
      <div className="block__inner">
        {(label || title || intro) && (
          <header className="block__header">
            <div className="block__header-inner ls-reversed-column">
              <h2 id={slugify(title)} className={title ? 'block__title title--l' : 'sr-text'}>
                {title || translate('Header:contact')}
              </h2>
              {label && <p className="block__label">{label}</p>}
            </div>
            {intro && <p className="block__intro">{intro}</p>}
          </header>
        )}

        <div className="block__content">
          {(address || body) && (
            <section className="block__row">
              {address.title && <h3 className="block__row-title title--m">{address.title}</h3>}

              <div className="block__column">
                <ul className="block__list">
                  {(body || (visiting && (visiting.street || visiting.country || visiting.mapLink))) && (
                    <li className="block__item">
                      <article className="block__sublist block__sublist--pin">
                        <h4 className="block__subitem block__subitem--bold">
                          <svg className="block__icon" height="25" width="20">
                            <use xlinkHref="#icon--pin" />
                          </svg>
                          {translate('Common:Address')}
                        </h4>
                        {body && <InnerHtmlWrapper className="block__subitem" text={body.replace(/\n/g, '<br>')} />}
                        {visiting.street && <p className="block__subitem">{visiting.street}</p>}
                        {visiting.code && <p className="block__subitem">{visiting.code}</p>}
                        {visiting.city && <p className="block__subitem">{visiting.city}</p>}
                        {visiting.country && <p className="block__subitem">{visiting.country}</p>}
                        {visiting.mapLink && (
                          <p className="block__subitem">
                            <InternalOrExternalLink {...visiting.mapLink} className="block__link">
                              {visiting.mapLink.title}
                            </InternalOrExternalLink>
                          </p>
                        )}
                      </article>
                    </li>
                  )}
                  {postal && (postal.address || postal.country) && (
                    <li className="block__item">
                      <ul className="block__sublist block__sublist--pin">
                        <li className="block__subitem block__subitem--bold">
                          <svg className="block__icon" height="25" width="20">
                            <use xlinkHref="#icon--pin" />
                          </svg>
                          {translate('Common:Postaladdress')}
                        </li>
                        <>
                          {postal.address && (
                            <InnerHtmlWrapper className="block__subitem" text={postal.address.replace(/\n/g, '<br>')} />
                          )}
                          {postal.street && <li className="block__subitem">{postal.street}</li>}
                          {postal.code && <li className="block__subitem">{postal.code}</li>}
                          {postal.city && <li className="block__subitem">{postal.city}</li>}
                          {postal.country && <li className="block__subitem">{postal.country}</li>}
                        </>
                      </ul>
                    </li>
                  )}
                </ul>
              </div>
            </section>
          )}

          {(contact.telephone || contact.email || contact.fax || contact.website) && (
            <section className="block__row">
              <h4 className="block__row-title title--s">{contact.title}</h4>
              <div className="block__column">
                <ul className="block__list">
                  {contact.telephone && contact.telephone.length > 0 && (
                    <li className="block__item">
                      <ul className="block__sublist block__sublist--phone">
                        <li className="block__subitem block__subitem--bold">
                          <svg className="block__icon" height="22" width="22">
                            <use xlinkHref="#icon--phone" />
                          </svg>
                          {translate('Common:Phone')}
                        </li>
                        {contact.telephone.map((number) => (
                          <li className="block__subitem">
                            <a href={`tel:${extractPhoneNumber(number)}`}>{number}</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                  )}
                  {contact.email && contact.email.length > 0 && (
                    <li className="block__item">
                      <ul className="block__sublist block__sublist--mail">
                        <li className="block__subitem block__subitem--bold">
                          <svg className="block__icon" height="18" width="22">
                            <use xlinkHref="#icon--mail" />
                          </svg>
                          {translate('Common:Email')}
                        </li>
                        {contact.email.map((emailaddr) => (
                          <li className="block__subitem">
                            <a href={`mailto:${emailaddr}`}>{emailaddr}</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                  )}
                  {contact.fax && contact.fax.length > 0 && (
                    <li className="block__item">
                      <ul className="block__sublist block__sublist--fax">
                        <li className="block__subitem block__subitem--bold">
                          <svg className="block__icon" height="22" width="18">
                            <use xlinkHref="#icon--fax" />
                          </svg>
                          {translate('Common:Fax')}
                        </li>
                        {contact.fax.map((faxnum) => (
                          <li className="block__subitem">
                            <a href={`tel:${extractPhoneNumber(faxnum)}`}>{faxnum}</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                  )}
                  {contact.website && (
                    <li className="block__item">
                      <ul className="block__sublist block__sublist--web">
                        <li className="block__subitem block__subitem--bold">
                          <svg className="block__icon" height="22" width="22">
                            <use xlinkHref="#icon--web" />
                          </svg>
                          Website
                        </li>
                        <li className="block__subitem">
                          <InternalOrExternalLink {...contact.website} className="block__link">
                            {contact.website.title}
                          </InternalOrExternalLink>
                        </li>
                      </ul>
                    </li>
                  )}
                </ul>
              </div>
            </section>
          )}
        </div>
      </div>
    </section>
  ) : (
    <section id={id} ref={ref} className={cn('block block--office ls-block-spacing', inView && 'inview')}>
      <div className="block__inner ls-container">
        <div className="block__layout ls-columns">
          {(label || title || intro) && (
            <header className="block__header ls-columns__header">
              <div className="block__header-inner ls-reversed-column">
                <h2 id={slugify(title)} className={title ? 'block__title title--l' : 'sr-text'}>
                  {title || translate('Header:contact')}
                </h2>
                {label && <p className="block__label">{label}</p>}
              </div>
              {intro && <p className="block__intro">{intro}</p>}
            </header>
          )}

          <div className="block__content ls-columns__main">
            {(address || body) && (
              <section className="block__row">
                {address.title && <h3 className="block__row-title title--m">{address.title}</h3>}

                <div className="block__column">
                  <ul className="block__list">
                    {(body || (visiting && (visiting.street || visiting.country || visiting.mapLink))) && (
                      <li className="block__item">
                        <article className="block__sublist block__sublist--pin">
                          <h4 className="block__subitem block__subitem--bold">
                            <svg className="block__icon" height="25" width="20">
                              <use xlinkHref="#icon--pin" />
                            </svg>
                            {translate('Common:Address')}
                          </h4>
                          {body && <InnerHtmlWrapper className="block__subitem" text={body.replace(/\n/g, '<br>')} />}
                          {visiting.street && <p className="block__subitem">{visiting.street}</p>}
                          {visiting.code && <p className="block__subitem">{visiting.code}</p>}
                          {visiting.city && <p className="block__subitem">{visiting.city}</p>}
                          {visiting.country && <p className="block__subitem">{visiting.country}</p>}
                          {visiting.mapLink && (
                            <p className="block__subitem">
                              <InternalOrExternalLink {...visiting.mapLink} className="block__link">
                                {visiting.mapLink.title}
                              </InternalOrExternalLink>
                            </p>
                          )}
                        </article>
                      </li>
                    )}
                    {postal && (postal.address || postal.country) && (
                      <li className="block__item">
                        <ul className="block__sublist block__sublist--pin">
                          <li className="block__subitem block__subitem--bold">
                            <svg className="block__icon" height="25" width="20">
                              <use xlinkHref="#icon--pin" />
                            </svg>
                            {translate('Common:Postaladdress')}
                          </li>
                          <>
                            {postal.address && (
                              <InnerHtmlWrapper
                                className="block__subitem"
                                text={postal.address.replace(/\n/g, '<br>')}
                              />
                            )}
                            {postal.street && <li className="block__subitem">{postal.street}</li>}
                            {postal.code && <li className="block__subitem">{postal.code}</li>}
                            {postal.city && <li className="block__subitem">{postal.city}</li>}
                            {postal.country && <li className="block__subitem">{postal.country}</li>}
                          </>
                        </ul>
                      </li>
                    )}
                  </ul>
                </div>
              </section>
            )}

            {(contact.telephone || contact.email || contact.fax || contact.website) && (
              <section className="block__row">
                <h4 className="block__row-title title--s">{contact.title}</h4>
                <div className="block__column">
                  <ul className="block__list">
                    {contact.telephone && contact.telephone.length > 0 && (
                      <li className="block__item">
                        <ul className="block__sublist block__sublist--phone">
                          <li className="block__subitem block__subitem--bold">
                            <svg className="block__icon" height="22" width="22">
                              <use xlinkHref="#icon--phone" />
                            </svg>
                            {translate('Common:Phone')}
                          </li>
                          {contact.telephone.map((number) => (
                            <li className="block__subitem">
                              <a href={`tel:${extractPhoneNumber(number)}`}>{number}</a>
                            </li>
                          ))}
                        </ul>
                      </li>
                    )}
                    {contact.email && contact.email.length > 0 && (
                      <li className="block__item">
                        <ul className="block__sublist block__sublist--mail">
                          <li className="block__subitem block__subitem--bold">
                            <svg className="block__icon" height="18" width="22">
                              <use xlinkHref="#icon--mail" />
                            </svg>
                            {translate('Common:Email')}
                          </li>
                          {contact.email.map((emailaddr) => (
                            <li className="block__subitem">
                              <a href={`mailto:${emailaddr}`}>{emailaddr}</a>
                            </li>
                          ))}
                        </ul>
                      </li>
                    )}
                    {contact.fax && contact.fax.length > 0 && (
                      <li className="block__item">
                        <ul className="block__sublist block__sublist--fax">
                          <li className="block__subitem block__subitem--bold">
                            <svg className="block__icon" height="22" width="18">
                              <use xlinkHref="#icon--fax" />
                            </svg>
                            {translate('Common:Fax')}
                          </li>
                          {contact.fax.map((faxnum) => (
                            <li className="block__subitem">
                              <a href={`tel:${extractPhoneNumber(faxnum)}`}>{faxnum}</a>
                            </li>
                          ))}
                        </ul>
                      </li>
                    )}
                    {contact.website && (
                      <li className="block__item">
                        <ul className="block__sublist block__sublist--web">
                          <li className="block__subitem block__subitem--bold">
                            <svg className="block__icon" height="22" width="22">
                              <use xlinkHref="#icon--web" />
                            </svg>
                            Website
                          </li>
                          <li className="block__subitem">
                            <InternalOrExternalLink {...contact.website} className="block__link">
                              {contact.website.title}
                            </InternalOrExternalLink>
                          </li>
                        </ul>
                      </li>
                    )}
                  </ul>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

const extractPhoneNumber = (telephone: string) => {
  let s = ''
  telephone = telephone.replace(/\(/g, '')
  telephone = telephone.replace(/\)/g, '')
  telephone = telephone.replace(' ', '')
  for (let i = 0; i < telephone.length; i++) {
    const element = telephone[i]
    if (element == '+' || element == '-' || element == ' ' || !isNaN(+element)) {
      s += element
    } else {
      return s.trim()
    }
  }
  return telephone.trim()
}
